/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { useDispatch } from 'react-redux';
import { BlIconType } from '@blend/shared-react';
import { MaterialIcon } from '@blend/enterprise-components';
import { Card, colors, fonts, IconButton, mixins } from '@blend/consumer-components';
import { removeNotification } from '../../../actions/pageActions';
import { useConsumerTheme } from '../../../hooks/useConsumerTheme';

const DISAPPEAR_ANIMATION_DURATION = 0.5; // in seconds
export const AUTO_DISAPPEAR_DELAY = 5000;

const GENERIC_API_ERROR_MESSAGE = 'That action could not be completed. Try again.';

const styles = StyleSheet.create({
  notificationItem: {
    marginTop: 12,
    opacity: 1,
    ...mixins.transition('opacity', DISAPPEAR_ANIMATION_DURATION),
  },
  removingNotificationItem: {
    opacity: 0,
  },
  notificationContent: {
    display: 'flex',
    justifyContent: 'space-between',
    width: 349,
    minHeight: 48,
    ...mixins.padding(12),
  },
  contentWrapper: {
    width: '75%',
    ...mixins.marginHorizontal(10),
  },
  fullWidthContentWrapper: {
    width: '100%',
  },
  footerWrapper: {
    ...mixins.margin(12, 0, 0, 0),
    cursor: 'pointer',
    fontWeight: fonts.weight.medium,
  },
  buttonWrapper: {
    alignSelf: 'flex-start',
  },
  info: {
    backgroundColor: colors.reflexLight08,
  },
  affirm: {
    backgroundColor: colors.aliceishBlue,
  },
  error: {
    backgroundColor: colors.brickMinus10,
  },
});

export namespace NotificationItem {
  export interface Props {
    id: string;
    type: 'info' | 'affirm' | 'error';
    iconType: BlIconType;
    content?: string;
    action?: string;
    handleActionClick?: () => void;
    shouldAutoRemove: boolean;
    removing?: boolean; // used for animation
    disallowDismiss?: boolean; // remove the 'x' button so that the user can't dismiss the notification
  }

  export type PropsWithoutId = Omit<NotificationItem.Props, 'id'>;
}

export const NotificationItem: React.FC<NotificationItem.Props> = ({
  id,
  type,
  iconType,
  content = GENERIC_API_ERROR_MESSAGE,
  action,
  handleActionClick,
  removing = false,
  shouldAutoRemove,
  disallowDismiss,
}) => {
  const dispatch = useDispatch();
  const { appConfig } = useConsumerTheme();

  const highlightColors = {
    info: colors.reflex,
    affirm: colors.grass,
    error: colors.brickMinus4,
  };

  const autoHide = () => {
    if (shouldAutoRemove) {
      setTimeout(() => {
        removeNotification(id)(dispatch);
      }, AUTO_DISAPPEAR_DELAY);
    }
  };

  const handleRemoveClick = (notificationId: string): void => {
    removeNotification(notificationId)(dispatch);
  };

  useEffect(autoHide, []);

  return (
    <div
      className={css(styles.notificationItem, removing ? styles.removingNotificationItem : undefined)}
      data-it="NotificationItem"
    >
      <Card borderType="shadow" borderHighlightSide="left" borderHighlightColor={highlightColors[type]}>
        <div className={css(styles.notificationContent, styles[type])}>
          <MaterialIcon type={iconType as any} size={24} color={highlightColors[type]} />
          <div className={css(styles.contentWrapper, disallowDismiss && styles.fullWidthContentWrapper)}>
            <div>{content}</div>
            <div
              className={css(styles.footerWrapper)}
              style={{ color: highlightColors[type] }}
              onClick={handleActionClick}
              data-it={`notification-action-${type}`}
            >
              {action}
            </div>
          </div>
          {!disallowDismiss ? (
            <div className={css(styles.buttonWrapper)}>
              <IconButton icon="delete" onClick={() => handleRemoveClick(id)} actionColor={appConfig.colors.action} />
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
};
